import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ACCOUNT } from 'src/app/accounts';
import { TransactionsService } from 'src/app/services/transactions.service';
import { InvoicePromptDialogComponent } from 'src/app/dialogs/invoice-prompt/invoice-prompt.dialog';
import { ChatGPTService } from 'src/app/services/chatgpt.service';
import { LineItem } from '@contracts/interfaces/lineitem.interface';
import { InvoicesService } from 'src/app/services/invoices.service';
import { HttpClient } from '@angular/common/http';
import { ConnectionService } from 'src/app/services/connection.service';

@Component({
  selector: 'app-tests',
  templateUrl: './tests.component.html',
  styleUrls: ['./tests.component.scss']
})
export class TestsComponent implements OnInit {

  public revenueState: 'idle' | 'loading' | 'done' | 'error' = 'idle';
  public revenueCount: number | null = null;
  public revenueError: string | null = null;
  public revenueLastRun: Date | null = null;

  public promptDraft = 'Draft an invoice for a MacBook Pro repair with parts, labour, and pickup ETA.';
  public promptLog: { text: string; at: Date }[] = [];
  public aiInvoiceLoading = false;
  public aiInvoiceError: string | null = null;

  public validateInvoicesState: 'idle' | 'loading' | 'done' | 'error' = 'idle';
  public validateInvoicesResult: { total?: number; updated?: number; statusCounts?: Record<string, number>; updatedInvoices?: any[] } | null = null;
  public validateInvoicesError: string | null = null;

  public invoiceStatusState: 'idle' | 'loading' | 'done' | 'error' = 'idle';
  public invoiceStatusResult: Record<string, number> | null = null;
  public invoiceStatusError: string | null = null;

  public webflowState: 'idle' | 'loading' | 'done' | 'error' = 'idle';
  public webflowSiteId = '';
  public webflowFormId = '';
  public webflowLimit = 10;
  public webflowOffset = 0;
  public webflowForms: any[] = [];
  public webflowSubmissions: any[] = [];
  public webflowError: string | null = null;
  public webflowPagination: any = null;

  public testShortcuts = [
    {
      title: 'Workload forecasting',
      description: 'Explore historical throughput, backlog scenarios and individual repair forecasts.',
      route: '/tests/workload-forecast',
      icon: 'insights'
    },
    {
      title: 'Square API',
      description: 'Test the Square connection and browse transfers by location and date.',
      route: '/tests/square-tests',
      icon: 'payments'
    },
    {
      title: 'Dialog lab',
      description: 'Launch every dialog from one page and compare window styling.',
      route: '/tests/dialog-lab',
      icon: 'web_asset'
    },
    {
      title: 'PDF rendering lab',
      description: 'Generate and review PDFs before wiring them to automation.',
      route: '/tests/pdf-tests',
      icon: 'picture_as_pdf'
    },
    {
      title: 'Email flows',
      description: 'Test outbound email copy and delivery states.',
      route: '/tests/email-tests',
      icon: 'mail'
    },
    {
      title: 'SMS flows',
      description: 'Dry-run SMS templates and delivery logging.',
      route: '/tests/sms-tests',
      icon: 'sms'
    },
    {
      title: 'Template sandbox',
      description: 'Preview template variables and rendering.',
      route: '/tests/template-tests',
      icon: 'view_quilt'
    },
    {
      title: 'Bank transfer parsing',
      description: 'Validate revenue detection for bank imports.',
      route: '/tests/bank-transfer-tests',
      icon: 'account_balance'
    },
    {
      title: 'Job imports',
      description: 'Check ingest quality on job batches.',
      route: '/tests/job-tests',
      icon: 'list_alt'
    },
    {
      title: 'eBay revenue ingest',
      description: 'Inspect marketplace statements before syncing.',
      route: '/tests/ebay-tests',
      icon: 'shopping_cart'
    },
    {
      title: 'QR generator',
      description: 'Skeleton page for QR input, settings, and preview tests.',
      route: '/tests/qr-generator-tests',
      icon: 'qr_code_2'
    },
    {
      title: 'Music Player Daemon',
      description: 'Check the MPD connection and control playback.',
      route: '/tests/mpd-tests',
      icon: 'speaker'
    }
  ];

  public signals = [
    { label: 'OpenAI', status: 'Queued', detail: 'Wire prompt-to-invoice to live API', tone: 'amber' },
    { label: 'PDF service', status: 'Ready', detail: 'Using local renderer', tone: 'green' },
    { label: 'Email/SMS', status: 'Ready', detail: 'Pointing at sandbox accounts', tone: 'green' },
    { label: 'Revenue feed', status: 'Beta', detail: 'Pulling REVENUE account data', tone: 'blue' }
  ];

  constructor(
    private transactionService: TransactionsService,
    private router: Router,
    private dialog: MatDialog,
    private chatGPTService: ChatGPTService,
    private invoicesService: InvoicesService,
    private http: HttpClient,
    private connectionService: ConnectionService
  ) { }

  ngOnInit(): void {}

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }

  validateAllInvoices(): void {
    if (this.validateInvoicesState === 'loading') {
      return;
    }

    this.validateInvoicesState = 'loading';
    this.validateInvoicesError = null;
    this.validateInvoicesResult = null;

    this.invoicesService.validateAll().subscribe({
      next: (result: any) => {
        this.validateInvoicesState = 'done';
        this.validateInvoicesResult = result;
      },
      error: (err) => {
        this.validateInvoicesState = 'error';
        this.validateInvoicesError = err?.error?.message ?? 'Failed to validate invoices.';
      }
    });
  }

  fetchInvoiceStatuses(): void {
    if (this.invoiceStatusState === 'loading') {
      return;
    }

    this.invoiceStatusState = 'loading';
    this.invoiceStatusError = null;
    this.invoiceStatusResult = null;

    this.invoicesService.getStatusValues().subscribe({
      next: (result: any) => {
        this.invoiceStatusState = 'done';
        this.invoiceStatusResult = result?.statusCounts ?? {};
      },
      error: (err) => {
        this.invoiceStatusState = 'error';
        this.invoiceStatusError = err?.error?.message ?? 'Failed to fetch invoice statuses.';
      }
    });
  }

  openChatGPTInvoice(): void {
    if (this.aiInvoiceLoading) {
      return;
    }

    const dialogRef = this.dialog.open(InvoicePromptDialogComponent, {
      width: '600px',
      data: {
        prompt: this.promptDraft
      }
    });

    dialogRef.afterClosed().subscribe((prompt: string | undefined) => {
      if (!prompt) {
        return;
      }

      this.generateInvoiceFromPrompt(prompt);
    });
  }

  openBlankInvoiceDialog(event?: MouseEvent): void {
    event?.stopPropagation();
    this.invoicesService.openCreateInvoiceDialog('standard', () => {});
  }

  private generateInvoiceFromPrompt(prompt: string): void {
    this.aiInvoiceLoading = true;
    this.aiInvoiceError = null;

    const instruction = `
You are an invoicing assistant. Given a description of work, return JSON only with no prose.
Response shape: {
  "customerName": "ChatGPT",
  "notes": "any short note",
  "lineItems": [
    { "item": "Service", "desc": "Work performed", "qty": 1, "price": 100 },
    { "item": "Part", "desc": "Part description", "qty": 1, "price": 70 }
  ]
}
Rules:
- Prices are AUD numbers expressed in cents (integer, no currency symbols).
- Always include one Service line item summarising labour.
- Include each part as its own line item.
- qty is numeric (e.g., 1, 1.5).
- Do not return code fences or extra text. JSON only.`;

    const message = `${instruction}\n\nUser prompt:\n${prompt}`;

    this.chatGPTService.prompt(message, 'gpt-4o-mini').subscribe({
      next: (resp: any) => {
        const draft = this.parseInvoiceDraft(resp?.content ?? resp, prompt);

        this.router.navigate(
          ['/create-invoice', 'standard'],
          {
            queryParams: {
              prefillCustomer: 'ChatGPT',
              prefillEmail: 'support@openai.com',
              prefillPhone: '000-000-000',
              prefillNotes: 'LLM-generated invoice draft from Tests playground.',
              prompt
            },
            state: {
              aiDraft: draft
            }
          }
        );
        this.aiInvoiceLoading = false;
      },
      error: (err) => {
        this.aiInvoiceLoading = false;
        this.aiInvoiceError = err?.error?.message ?? 'Failed to generate invoice from prompt.';
      }
    });
  }

  private parseInvoiceDraft(content: any, fallbackDesc: string): { lineItems: LineItem[]; notes?: string } {
    let text = '';

    if (typeof content === 'string') {
      text = content;
    } else if (content?.content) {
      text = content.content;
    }

    const cleaned = text.replace(/```json|```/gi, '').trim();

    let parsed: any;
    try {
      const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
      parsed = JSON.parse(jsonMatch ? jsonMatch[0] : cleaned);
    } catch (_) {
      parsed = null;
    }

    const rawItems: any[] = parsed?.lineItems ?? [];
    const lineItems: LineItem[] = rawItems
      .map((li) => ({
        item: li?.item || 'Service',
        desc: li?.desc || fallbackDesc,
        qty: Number(li?.qty) || 1,
        price: this.toCents(li?.price)
      }))
      .filter((li) => !!li.desc);

    if (!lineItems.length) {
      lineItems.push({
        item: 'Service',
        desc: fallbackDesc,
        qty: 1,
        price: 0
      });
    }

    return {
      lineItems,
      notes: parsed?.notes
    };
  }

  private toCents(value: any): number {
    const num = Number(value);
    if (isNaN(num)) {
      return 0;
    }
    return Math.round(num * 100);
  }

  recordPrompt(): void {
    const trimmed = this.promptDraft.trim();

    if (!trimmed) {
      return;
    }

    this.promptLog = [
      { text: trimmed, at: new Date() },
      ...this.promptLog
    ].slice(0, 6);
  }

  getTransactions(): void {
    this.revenueState = 'loading';
    this.revenueError = null;

    this.transactionService.find({ 'source.account': ACCOUNT.REVENUE }).subscribe({
      next: (transactions: any[]) => {
        this.revenueState = 'done';
        this.revenueCount = transactions?.length ?? 0;
        this.revenueLastRun = new Date();
        console.log('Revenue Transactions:', transactions);
      },
      error: (err) => {
        this.revenueState = 'error';
        this.revenueError = err?.message ?? 'Failed to fetch revenue transactions';
      }
    });
  }

  pullWebflowSubmissions(): void {
    if (this.webflowState === 'loading') {
      return;
    }

    this.webflowState = 'loading';
    this.webflowError = null;
    this.webflowForms = [];
    this.webflowSubmissions = [];
    this.webflowPagination = null;

    const body: any = {
      limit: this.webflowLimit ?? undefined,
      offset: this.webflowOffset ?? undefined
    };

    if (this.webflowSiteId.trim()) {
      body.siteId = this.webflowSiteId.trim();
    }

    if (this.webflowFormId.trim()) {
      body.formId = this.webflowFormId.trim();
    }

    this.http.post<any>(`${this.connectionService.address}/webflow/test`, body).subscribe({
      next: (result) => {
        this.webflowState = 'done';
        this.webflowForms = result?.forms ?? [];
        this.webflowSubmissions = result?.submissions ?? [];
        this.webflowPagination = result?.pagination ?? null;
        this.webflowSiteId = result?.siteId || this.webflowSiteId;
        this.webflowFormId = result?.formId || this.webflowFormId;
      },
      error: (err) => {
        this.webflowState = 'error';
        this.webflowError = err?.error?.detail || err?.message || 'Failed to pull Webflow submissions.';
      }
    });
  }
}
